import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, Filter, Search, ChevronDown } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { getAnomalies, getKPIs } from '../lib/api';
import { cn } from '../lib/utils';

const getSeverityStyles = (severity) => {
    switch (severity) {
        case 'critical':
            return 'severity-critical';
        case 'high':
            return 'severity-high';
        case 'medium':
            return 'severity-medium';
        default:
            return 'severity-low';
    }
};

export const Anomalies = () => {
    const navigate = useNavigate();
    const [anomalies, setAnomalies] = useState([]);
    const [kpis, setKpis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSeverity, setSelectedSeverity] = useState(null);
    const [selectedKPI, setSelectedKPI] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadData();
    }, [selectedSeverity, selectedKPI]);

    const loadData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedSeverity) params.severity = selectedSeverity;
            if (selectedKPI) params.kpi_id = selectedKPI;

            const [anomaliesRes, kpisRes] = await Promise.all([
                getAnomalies(params),
                getKPIs()
            ]);

            setAnomalies(anomaliesRes.data);
            setKpis(kpisRes.data);
        } catch (error) {
            console.error('Failed to load anomalies:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredAnomalies = anomalies.filter(a => 
        a.kpi_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const severityOptions = ['critical', 'high', 'medium', 'low'];

    return (
        <div className="space-y-6 animate-fade-in" data-testid="anomalies-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-card-foreground flex items-center gap-3">
                        <AlertTriangle className="h-8 w-8 text-destructive" />
                        Anomalies
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {anomalies.length} anomalies detected
                    </p>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-card border-border">
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by KPI name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 bg-background"
                                data-testid="search-anomalies"
                            />
                        </div>
                        
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="min-w-[150px]" data-testid="filter-severity">
                                    <Filter className="h-4 w-4 mr-2" />
                                    {selectedSeverity ? (
                                        <span className="capitalize">{selectedSeverity}</span>
                                    ) : (
                                        'Severity'
                                    )}
                                    <ChevronDown className="h-4 w-4 ml-2" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setSelectedSeverity(null)}>
                                    All Severities
                                </DropdownMenuItem>
                                {severityOptions.map((severity) => (
                                    <DropdownMenuItem 
                                        key={severity}
                                        onClick={() => setSelectedSeverity(severity)}
                                    >
                                        <span className="capitalize">{severity}</span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="min-w-[150px]" data-testid="filter-kpi">
                                    {selectedKPI ? (
                                        kpis.find(k => k.id === selectedKPI)?.name || 'KPI'
                                    ) : (
                                        'All KPIs'
                                    )}
                                    <ChevronDown className="h-4 w-4 ml-2" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setSelectedKPI(null)}>
                                    All KPIs
                                </DropdownMenuItem>
                                {kpis.map((kpi) => (
                                    <DropdownMenuItem 
                                        key={kpi.id}
                                        onClick={() => setSelectedKPI(kpi.id)}
                                    >
                                        {kpi.name}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </CardContent>
            </Card>

            {/* Anomalies List */}
            {loading ? (
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-24" />
                    ))}
                </div>
            ) : filteredAnomalies.length === 0 ? (
                <Card className="bg-card border-border">
                    <CardContent className="py-12 text-center">
                        <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-muted-foreground">No anomalies found</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredAnomalies.map((anomaly) => (
                        <Card 
                            key={anomaly.id}
                            className={cn(
                                "bg-card border-border cursor-pointer transition-all duration-200",
                                "hover:border-primary/50 hover:-translate-y-0.5"
                            )}
                            onClick={() => navigate(`/anomalies/${anomaly.id}`)}
                            data-testid={`anomaly-card-${anomaly.id}`}
                        >
                            <CardContent className="p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <Badge 
                                            variant="outline" 
                                            className={cn("text-xs border px-3 py-1", getSeverityStyles(anomaly.severity))}
                                        >
                                            <span className="capitalize">{anomaly.severity}</span>
                                        </Badge>
                                        <div>
                                            <p className="font-semibold text-card-foreground">{anomaly.kpi_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {format(new Date(anomaly.date), 'MMMM d, yyyy')}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-8">
                                        <div className="text-right">
                                            <p className="label-text">Actual Value</p>
                                            <p className="font-mono text-lg text-card-foreground">
                                                {anomaly.value.toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="label-text">Expected</p>
                                            <p className="font-mono text-lg text-muted-foreground">
                                                {anomaly.expected_value.toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="label-text">Z-Score</p>
                                            <p className={cn(
                                                "font-mono text-lg",
                                                Math.abs(anomaly.z_score) >= 3 ? "text-destructive" : "text-orange-400"
                                            )}>
                                                {anomaly.z_score.toFixed(2)}σ
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Anomalies;
